/**
 * pushNotifications.js
 *
 * Registers the device for push and stores the token against the user.
 * Call initPushNotifications() once the user is authenticated.
 *
 * A no-op on web: the Capacitor plugin does nothing there, so this is safe to
 * call on every platform.
 *
 * Every step records what happened in a module-level record that
 * getPushStatus() returns. Nothing else in this file can report a failure: the
 * only channel it used to have was console.error, and Capacitor strips the
 * JavaScript console from release builds, so a registration that failed on a
 * TestFlight device left no trace anywhere. The 1.2.1 build had zero rows in
 * users.push_token across every account and no way to find out why.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '../supabase/supabase';

// What happened on this launch, for the profile screen to display.
const status = {
  platform: null,
  permission: null,
  listenersAttached: false,
  registerCalled: false,
  tokenReceived: false,
  tokenSaved: false,
  lastError: null,
  updatedAt: null,
};

const record = (fields) => {
  Object.assign(status, fields, { updatedAt: new Date().toISOString() });
};

export function getPushStatus() {
  return { ...status };
}

async function saveToken(token) {
  try {
    record({ tokenReceived: true });

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      record({ lastError: 'Token arrived with no signed-in user' });
      return;
    }

    const { data, error } = await supabase
      .from('users')
      .update({
        push_token: token,
        // Which service the token belongs to. iOS returns an APNs token and
        // Android an FCM one, and they are not interchangeable: a sender has to
        // know which one it is holding.
        push_platform: Capacitor.getPlatform(),
        push_updated_at: new Date().toISOString(),
      })
      .eq('id', user.id)
      .select('id');

    if (error) {
      record({ lastError: `Save failed: ${error.message}` });
      return;
    }
    // An update that matches no row returns success with an empty array, which
    // is what a row-level security refusal looks like from the client.
    if (!data || data.length === 0) {
      record({ lastError: 'Save matched no row' });
      return;
    }

    record({ tokenSaved: true, lastError: null });
  } catch (err) {
    record({ lastError: `Save threw: ${err.message}` });
  }
}

// The one in-flight run. initPushNotifications is called from two places in
// AuthContext on every launch (the getSession result and the auth state
// change), and they fire within the same tick. Without this the second caller
// sees listeners as already attached, skips the await, and races ahead to
// register() while the first call is still attaching them. A token delivered
// in that window has nothing listening for it and is gone: iOS does not
// redeliver it, and the app never learns it had one.
let inFlight = null;

async function run() {
  if (!Capacitor.isNativePlatform()) {
    record({ platform: 'web', lastError: 'Not a native platform' });
    return getPushStatus();
  }

  record({ platform: Capacitor.getPlatform() });

  try {
    // Attached, and AWAITED, before register().
    //
    // addListener returns a Promise in Capacitor 5 and later: attaching a
    // listener is asynchronous. Firing these off without awaiting them and
    // then calling register() is a race, and the token loses it: iOS answers
    // registration almost immediately, and an event that arrives before the
    // JS listener exists is dropped with nothing logged anywhere.
    await Promise.all([
      PushNotifications.addListener('registration', (token) => saveToken(token.value)),

      PushNotifications.addListener('registrationError', (err) => {
        record({ lastError: `Registration failed: ${err?.error || JSON.stringify(err)}` });
      }),

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Push received:', notification);
      }),

      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('Push action:', action);
      }),
    ]);

    record({ listenersAttached: true });

    let permission = await PushNotifications.checkPermissions();
    record({ permission: permission.receive });

    // 'prompt-with-rationale' is an Android state meaning the user declined
    // once and the system wants an explanation before asking again. Both it
    // and 'prompt' mean "not yet decided enough to give up on".
    if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
      permission = await PushNotifications.requestPermissions();
      record({ permission: permission.receive });
    }

    // The old code re-read the ORIGINAL status here, which still said 'prompt'
    // after a user granted at the prompt, so it returned before registering
    // and a fresh install never produced a token at all.
    if (permission.receive !== 'granted') {
      record({ lastError: `Permission is ${permission.receive}` });
      return getPushStatus();
    }

    await PushNotifications.register();
    record({ registerCalled: true });
  } catch (err) {
    // Never let push break sign in.
    record({ lastError: `Setup threw: ${err.message}` });
    console.error('Push notification setup failed:', err);
  }

  return getPushStatus();
}

/**
 * Safe to call repeatedly and concurrently: every caller awaits the same run.
 *
 * @param {{force?: boolean}} [options] force starts a fresh run even if one
 *   has already completed, which is what the profile screen's retry uses.
 */
export function initPushNotifications(options = {}) {
  if (options.force) inFlight = null;
  if (!inFlight) inFlight = run();
  return inFlight;
}
